/**
 * Token storage (sessionStorage-based)
 */

import type { TokenSet } from "./types";

// Re-export TokenSet for backward compatibility
export type { TokenSet } from "./types";

const KEY = "tj.tokens.v1";

/**
 * Save tokens to storage
 */
export function saveTokens(tokens: TokenSet): void {
  sessionStorage.setItem(KEY, JSON.stringify(tokens));
}

/**
 * Read tokens from storage
 */
export function readTokens(): TokenSet | null {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenSet;
  } catch {
    return null;
  }
}

/**
 * Clear tokens from storage
 */
export function clearTokens(): void {
  sessionStorage.removeItem(KEY);
}

/**
 * Check if tokens are expired
 * @param tokens Token set to check
 * @param skewSec Buffer seconds before actual expiry (default: 60)
 */
export function isExpired(tokens?: TokenSet | null, skewSec = 60): boolean {
  if (!tokens) return true;
  const now = Math.floor(Date.now() / 1000);
  return now >= tokens.expires_at - skewSec;
}

/**
 * Get remaining time until expiry in seconds
 * Returns 0 if expired or no tokens
 */
export function getTimeToExpiry(tokens?: TokenSet | null): number {
  if (!tokens) return 0;
  const now = Math.floor(Date.now() / 1000);
  const remaining = tokens.expires_at - now;
  return remaining > 0 ? remaining : 0;
}
